import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;

function GraphicPreviewSplitScreen({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
  dominantImage = null,
  dominantImageScale = 100,
  dominantImageX = 50,
  dominantImageY = 50,
  dominantImageOpacity = 100,
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;
  const scale = (dominantImageScale ?? 100) / 100;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || '#555555';

  const isLightColor = (color) => {
    if (!color || color === 'null') return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  };

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(180deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(180deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const leftTextColor = isLightColor(teamColors.primary) ? '#1a202c' : '#ffffff';
  const pillBg = isLightColor(teamColors.primary)
    ? 'rgba(0,0,0,0.15)'
    : 'rgba(255,255,255,0.2)';

  const getOpponentColor = () => {
    if (isLightColor(teamColors.secondary)) {
      return isLightColor(teamColors.primary) ? '#2d3748' : teamColors.primary;
    }
    return teamColors.secondary;
  };

  const pillFontSize = Math.max(28, Math.min(Math.round(heroFontSize / 4), 56));

  const logoCircleStyle = {
    width: 200,
    height: 200,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const logoImgStyle = {
    width: 'auto',
    height: 'auto',
    maxWidth: '80%',
    maxHeight: '80%',
  };

  return (
    <div
      id="graphic-preview"
      style={{
        width: W,
        height: H,
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
        background: 'white',
        fontFamily: 'Barlow, sans-serif',
        border: '2px solid #e2e8f0',
        borderRadius: 16,
      }}
    >
      {/* Left Panel */}
      <div
        style={{
          width: 400,
          height: H,
          background: getBackground(),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '60px 32px',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* GAME DAY Pill */}
        <div
          style={{
            background: pillBg,
            color: leftTextColor,
            fontFamily: heroFont,
            fontSize: pillFontSize,
            fontWeight: 700,
            letterSpacing: 3,
            padding: '14px 36px',
            borderRadius: 40,
            marginBottom: 60,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          GAME DAY
        </div>

        {/* Logos + VS */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 32,
          }}
        >
          {/* Home Logo */}
          <div style={logoCircleStyle}>
            {homeTeamLogo ? (
              <img
                src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                alt={homeTeam}
                style={logoImgStyle}
              />
            ) : (
              <span style={{ fontSize: 100 }}>
                {teamSport === 'Softball' ? '🥎' : '⚾'}
              </span>
            )}
          </div>

          {/* VS */}
          <div
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 64,
              fontWeight: 900,
              color: leftTextColor,
              opacity: 0.9,
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
              letterSpacing: 4,
            }}
          >
            VS
          </div>

          {/* Away Logo */}
          <div style={logoCircleStyle}>
            {awayTeamLogoPreview ? (
              <img
                src={awayTeamLogoPreview}
                alt={awayTeam}
                style={logoImgStyle}
              />
            ) : (
              <span style={{ fontSize: 100 }}>
                {teamSport === 'Softball' ? '🥎' : '⚾'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Accent Stripe */}
      <div
        style={{
          width: 10,
          height: H,
          background: accent,
          flexShrink: 0,
        }}
      />

      {/* Right Panel */}
      <div
        style={{
          flex: 1,
          height: H,
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '80px 64px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Dominant Image */}
        {dominantImage && (
          <img
            src={dominantImage}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${dominantImageY ?? 50}%`,
              left: `${dominantImageX ?? 50}%`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              maxWidth: 'none',
              opacity: (dominantImageOpacity ?? 100) / 100,
              zIndex: 0,
            }}
          />
        )}
        {/* Team Names */}
        <div style={{ marginBottom: 'auto' }}>
          <div
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: (homeTeam || 'YOUR TEAM').length > 12 ? 56 : (homeTeam || 'YOUR TEAM').length > 8 ? 68 : 80,
              fontWeight: 700,
              color: teamColors.primary,
              lineHeight: 1.1,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            {homeTeam || 'YOUR TEAM'}
          </div>
          <div
            style={{
              fontFamily: 'Barlow, sans-serif',
              fontSize: 36,
              fontWeight: 600,
              color: '#bbbbbb',
              letterSpacing: 6,
              marginBottom: 16,
            }}
          >
            VS
          </div>
          <div
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: (awayTeam || 'OPPONENT').length > 12 ? 50 : (awayTeam || 'OPPONENT').length > 8 ? 62 : 72,
              fontWeight: 700,
              color: getOpponentColor(),
              lineHeight: 1.1,
              letterSpacing: 2,
            }}
          >
            {awayTeam || 'OPPONENT'}
          </div>
        </div>

        {/* Detail Rows */}
        <div
          style={{
            borderTop: `4px solid ${accent}`,
            paddingTop: 48,
            marginTop: 60,
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
          }}
        >
          {gameDate && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                fontFamily: 'Barlow, sans-serif',
                fontSize: 32,
                fontWeight: 600,
                color: detailColor,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: accent,
                  flexShrink: 0,
                }}
              />
              {formatGameDate(gameDate, dateFormat)}
            </div>
          )}
          {gameTime && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                fontFamily: 'Barlow, sans-serif',
                fontSize: 32,
                fontWeight: 600,
                color: detailColor,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: accent,
                  flexShrink: 0,
                }}
              />
              {formatGameTime(gameTime)}
            </div>
          )}
          {location && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                fontFamily: 'Barlow, sans-serif',
                fontSize: 32,
                fontWeight: 600,
                color: detailColor,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: accent,
                  flexShrink: 0,
                }}
              />
              {location}
            </div>
          )}
        </div>

        {/* Watermark */}
        {showWatermark && (
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              background: 'rgba(255,255,255,0.92)',
              padding: '6px 12px',
              borderRadius: 8,
              pointerEvents: 'none',
            }}
          >
            <img
              src="/PMlogoleft.png"
              alt="Playmaker Media Kit"
              style={{ height: 80, width: 'auto', display: 'block' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default GraphicPreviewSplitScreen;
